import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { updateChat } from '@/services/api/chatbot';
import type { ChatbotChat } from '@/types/chatbot';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/hooks/use-toast';
import {
    User2, Palette, Layout as LayoutIcon, FileText,
    Code2, Check, Sparkles, Save,
} from 'lucide-react';

interface Preset {
    name: string;
    swatches: string[];      // 2-3 cores principais pro thumbnail
    primary_color: string;
    bg_color?: string;
    bubble_bg?: string;
    bubble_text?: string;
    button_style?: 'filled' | 'outlined' | 'gradient';
    bubble_style?: 'rounded' | 'pill' | 'square';
    layout?: 'mobile' | 'wide' | 'fullscreen';
}

const PRESETS: Preset[] = [
    {
        name: 'Wivlox',
        swatches: ['#4caf50', '#f1f3f5', '#ffffff'],
        primary_color: '#4caf50', bg_color: '#ffffff',
        bubble_bg: '#f1f3f5', bubble_text: '#1f2937',
        button_style: 'filled', bubble_style: 'pill', layout: 'mobile',
    },
    {
        name: 'Roxo SaaS',
        swatches: ['#7c3aed', '#f3f0ff', '#faf5ff'],
        primary_color: '#7c3aed', bg_color: '#faf5ff',
        bubble_bg: '#f3f0ff', bubble_text: '#1f2937',
        button_style: 'gradient', bubble_style: 'rounded', layout: 'mobile',
    },
    {
        name: 'Azul Corp',
        swatches: ['#2563eb', '#eff6ff', '#f0f9ff'],
        primary_color: '#2563eb', bg_color: '#f0f9ff',
        bubble_bg: '#eff6ff', bubble_text: '#1f2937',
        button_style: 'filled', bubble_style: 'rounded', layout: 'wide',
    },
    {
        name: 'Negativados',
        swatches: ['#dc2626', '#fef2f2', '#ffffff'],
        primary_color: '#dc2626', bg_color: '#fef2f2',
        bubble_bg: '#fee2e2', bubble_text: '#1f2937',
        button_style: 'filled', bubble_style: 'pill', layout: 'mobile',
    },
    {
        name: 'Dark',
        swatches: ['#22c55e', '#1f2937', '#111827'],
        primary_color: '#22c55e', bg_color: '#111827',
        bubble_bg: '#1f2937', bubble_text: '#e5e7eb',
        button_style: 'outlined', bubble_style: 'rounded', layout: 'fullscreen',
    },
];

export default function ThemePanel({ chat }: { chat: ChatbotChat }) {
    const { toast } = useToast();
    const queryClient = useQueryClient();

    const [form, setForm] = useState<Partial<ChatbotChat>>({});

    useEffect(() => {
        setForm({
            bot_name: chat.bot_name || '',
            bot_avatar_url: chat.bot_avatar_url || '',
            primary_color: chat.primary_color || '#22c55e',
            bg_color: chat.bg_color || '#ffffff',
            bubble_bg: chat.bubble_bg || '#f1f3f5',
            bubble_text: chat.bubble_text || '#1f2937',
            bubble_style: chat.bubble_style || 'rounded',
            button_style: chat.button_style || 'filled',
            layout: chat.layout || 'wide',
            show_header: chat.show_header ? 1 : 0,
            show_typing: chat.show_typing ?? 1,
            terms_url: chat.terms_url || '',
            privacy_url: chat.privacy_url || '',
            footer_html: chat.footer_html || '',
            custom_css: chat.custom_css || '',
        });
    }, [chat.id]);

    const saveMut = useMutation({
        mutationFn: () => updateChat(chat.id, {
            ...form,
            show_header: !!form.show_header,
            show_typing: !!form.show_typing,
        } as any),
        onSuccess: () => {
            toast({ title: 'Tema salvo!' });
            queryClient.invalidateQueries({ queryKey: ['chatbot-chats'] });
        },
    });

    const applyPreset = (p: Preset) => setForm({
        ...form,
        primary_color: p.primary_color,
        bg_color: p.bg_color ?? form.bg_color,
        bubble_bg: p.bubble_bg ?? form.bubble_bg,
        bubble_text: p.bubble_text ?? form.bubble_text,
        button_style: (p.button_style ?? form.button_style) as any,
        bubble_style: (p.bubble_style ?? form.bubble_style) as any,
        layout: (p.layout ?? form.layout) as any,
    });

    const isPresetActive = (p: Preset) =>
        form.primary_color === p.primary_color &&
        form.bg_color === p.bg_color &&
        form.bubble_bg === p.bubble_bg;

    return (
        <div className="grid grid-cols-12 gap-6">
            {/* ============ FORM ============ */}
            <div className="col-span-12 lg:col-span-7 space-y-4">

                {/* Presets - card destacado */}
                <Card>
                    <CardHeader icon={<Sparkles className="w-4 h-4" />} title="Presets de Tema"
                        subtitle="Comece a partir de um estilo pronto - voce pode ajustar tudo depois" />
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2.5 mt-3">
                        {PRESETS.map(p => {
                            const active = isPresetActive(p);
                            return (
                                <button key={p.name} type="button"
                                    onClick={() => applyPreset(p)}
                                    className={`group relative rounded-lg p-3 border-2 text-left transition-all ${
                                        active
                                            ? 'border-emerald-500 ring-2 ring-emerald-100 bg-emerald-50/40'
                                            : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                                    }`}>
                                    {/* swatch row */}
                                    <div className="flex gap-1 mb-2">
                                        {p.swatches.map((s, i) => (
                                            <div key={i}
                                                className="w-6 h-6 rounded-md shadow-sm border border-black/5"
                                                style={{ background: s }} />
                                        ))}
                                    </div>
                                    <div className="text-xs font-medium truncate">{p.name}</div>
                                    {active && (
                                        <div className="absolute top-1.5 right-1.5 w-5 h-5 bg-emerald-600 rounded-full flex items-center justify-center">
                                            <Check className="w-3 h-3 text-white" strokeWidth={3} />
                                        </div>
                                    )}
                                </button>
                            );
                        })}
                    </div>
                </Card>

                {/* Persona do bot */}
                <Card>
                    <CardHeader icon={<User2 className="w-4 h-4" />} title="Persona do bot"
                        subtitle="Nome e avatar do assistente (mostrado quando o header esta ativo)" />
                    <div className="grid grid-cols-2 gap-3 mt-3">
                        <FieldText label="Nome do bot" value={form.bot_name as string}
                            onChange={(v) => setForm({ ...form, bot_name: v })}
                            placeholder="Ex: Juliana" />
                        <FieldText label="Avatar URL" value={form.bot_avatar_url as string}
                            onChange={(v) => setForm({ ...form, bot_avatar_url: v })}
                            placeholder="https://..." />
                    </div>
                    <div className="mt-3 p-3 bg-slate-50 border border-slate-200 rounded-md text-xs text-slate-600 leading-relaxed">
                        <strong>Dica:</strong> pra introduzir o chat, crie perguntas do tipo{' '}
                        <span className="px-1.5 py-0.5 bg-white border rounded font-mono text-[10px]">Bloco de texto</span>{' '}
                        dentro do quiz - elas viram bolhas do bot que avancam sozinhas.
                    </div>
                </Card>

                {/* Cores */}
                <Card>
                    <CardHeader icon={<Palette className="w-4 h-4" />} title="Cores"
                        subtitle="Paleta usada pelo chat" />
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3">
                        <FieldColor label="Primaria" hint="Botoes & destaques"
                            value={form.primary_color as string}
                            onChange={(v) => setForm({ ...form, primary_color: v })} />
                        <FieldColor label="Fundo" hint="Background do app"
                            value={form.bg_color as string}
                            onChange={(v) => setForm({ ...form, bg_color: v })} />
                        <FieldColor label="Bolha" hint="Fundo da bolha do bot"
                            value={form.bubble_bg as string}
                            onChange={(v) => setForm({ ...form, bubble_bg: v })} />
                        <FieldColor label="Texto da bolha" hint="Cor do texto"
                            value={form.bubble_text as string}
                            onChange={(v) => setForm({ ...form, bubble_text: v })} />
                    </div>
                </Card>

                {/* Estilo / Layout */}
                <Card>
                    <CardHeader icon={<LayoutIcon className="w-4 h-4" />} title="Estilo & Layout"
                        subtitle="Formato dos elementos visuais" />

                    {/* Bubble style */}
                    <div className="mt-3 space-y-3">
                        <div>
                            <FieldLabel>Estilo da bolha</FieldLabel>
                            <SegmentedControl
                                value={form.bubble_style as string}
                                onChange={(v) => setForm({ ...form, bubble_style: v as any })}
                                options={[
                                    { value: 'rounded', label: 'Arredondada' },
                                    { value: 'pill', label: 'Pill' },
                                    { value: 'square', label: 'Quadrada' },
                                ]} />
                        </div>
                        <div>
                            <FieldLabel>Estilo do botao</FieldLabel>
                            <SegmentedControl
                                value={form.button_style as string}
                                onChange={(v) => setForm({ ...form, button_style: v as any })}
                                options={[
                                    { value: 'filled', label: 'Preenchido' },
                                    { value: 'outlined', label: 'Outline' },
                                    { value: 'gradient', label: 'Gradiente' },
                                ]} />
                        </div>
                        <div>
                            <FieldLabel>Layout</FieldLabel>
                            <SegmentedControl
                                value={form.layout as string}
                                onChange={(v) => setForm({ ...form, layout: v as any })}
                                options={[
                                    { value: 'mobile', label: 'Mobile' },
                                    { value: 'wide', label: 'Largo' },
                                    { value: 'fullscreen', label: 'Fullscreen' },
                                ]} />
                        </div>
                    </div>

                    {/* Switches */}
                    <div className="grid grid-cols-2 gap-3 mt-4 pt-4 border-t">
                        <ToggleRow
                            label="Mostrar header com avatar"
                            hint="Barra superior com nome+avatar do bot"
                            checked={!!form.show_header}
                            onChange={(v) => setForm({ ...form, show_header: v ? 1 : 0 })} />
                        <ToggleRow
                            label="Animacao de digitando"
                            hint="Mostra '...' antes de cada bolha"
                            checked={!!form.show_typing}
                            onChange={(v) => setForm({ ...form, show_typing: v ? 1 : 0 })} />
                    </div>
                </Card>

                {/* Footer */}
                <Card>
                    <CardHeader icon={<FileText className="w-4 h-4" />} title="Footer"
                        subtitle="Links de termos e privacidade no rodape" />
                    <div className="grid grid-cols-2 gap-3 mt-3">
                        <FieldText label="URL Termos" value={form.terms_url as string}
                            onChange={(v) => setForm({ ...form, terms_url: v })}
                            placeholder="/terms/" />
                        <FieldText label="URL Privacidade" value={form.privacy_url as string}
                            onChange={(v) => setForm({ ...form, privacy_url: v })}
                            placeholder="/privacy/" />
                    </div>
                    <div className="mt-3">
                        <FieldTextarea label="Footer HTML (opcional - sobrescreve os links acima)"
                            value={form.footer_html as string}
                            onChange={(v) => setForm({ ...form, footer_html: v })}
                            rows={2} placeholder="<small>&copy; 2025 ...</small>" />
                    </div>
                </Card>

                {/* Avancado */}
                <Card>
                    <CardHeader icon={<Code2 className="w-4 h-4" />} title="Avancado"
                        subtitle="Customizacoes CSS" />
                    <div className="mt-3">
                        <FieldTextarea label="Custom CSS"
                            hint="Injetado na pagina do chat - so use se souber o que esta fazendo"
                            value={form.custom_css as string}
                            onChange={(v) => setForm({ ...form, custom_css: v })}
                            rows={4} placeholder=".bubble { font-family: 'Inter', sans-serif; }" />
                    </div>
                </Card>

                {/* Save bar */}
                <div className="sticky bottom-4 bg-white border rounded-lg p-3 flex items-center justify-between shadow-lg">
                    <span className="text-sm text-slate-500">As mudancas precisam ser salvas pra aplicar</span>
                    <Button onClick={() => saveMut.mutate()} disabled={saveMut.isPending} size="default">
                        <Save className="w-4 h-4 mr-1.5" />
                        {saveMut.isPending ? 'Salvando...' : 'Salvar tema'}
                    </Button>
                </div>
            </div>

            {/* ============ PREVIEW ============ */}
            <div className="col-span-12 lg:col-span-5">
                <div className="sticky top-4">
                    <div className="flex items-center justify-between mb-3">
                        <span className="text-xs uppercase tracking-wide font-semibold text-slate-500">Preview</span>
                        <span className="text-[10px] text-slate-400">Atualiza em tempo real</span>
                    </div>
                    <PhonePreview form={form} />
                </div>
            </div>
        </div>
    );
}

/* ============ PREVIEW (phone frame) ============ */
function PhonePreview({ form }: { form: Partial<ChatbotChat> }) {
    const primary = form.primary_color || '#22c55e';
    const bubbleBg = form.bubble_bg || '#f1f3f5';
    const bubbleText = form.bubble_text || '#1f2937';
    const bgColor = form.bg_color || '#fff';

    const bubbleRadius = form.bubble_style === 'pill' ? '9999px'
        : form.bubble_style === 'square' ? '6px' : '16px';
    const btnRadius = form.button_style === 'outlined' ? '12px'
        : form.button_style === 'gradient' ? '14px' : '9999px';
    const btnBg = form.button_style === 'outlined' ? 'transparent'
        : form.button_style === 'gradient' ? `linear-gradient(135deg, ${primary}, color-mix(in srgb, ${primary} 70%, #000))`
        : primary;
    const btnColor = form.button_style === 'outlined' ? primary : '#fff';
    const btnBorder = form.button_style === 'outlined' ? `2px solid ${primary}` : 'none';

    return (
        <div className="relative mx-auto" style={{ maxWidth: 320 }}>
            {/* phone bezel */}
            <div className="rounded-[2rem] p-2 bg-slate-900 shadow-xl">
                <div className="rounded-[1.5rem] overflow-hidden relative" style={{ background: bgColor, minHeight: 540 }}>
                    {/* notch */}
                    <div className="absolute top-0 left-1/2 -translate-x-1/2 w-20 h-5 bg-slate-900 rounded-b-2xl z-10" />

                    {form.show_header ? (
                        <div className="px-4 pt-7 pb-3 flex items-center gap-2 border-b border-black/5"
                            style={{ background: 'rgba(255,255,255,.7)' }}>
                            <div className="w-8 h-8 rounded-full text-white flex items-center justify-center text-xs font-bold shadow-sm"
                                style={{
                                    background: primary,
                                    backgroundImage: form.bot_avatar_url ? `url(${form.bot_avatar_url})` : undefined,
                                    backgroundSize: 'cover', backgroundPosition: 'center'
                                }}>
                                {!form.bot_avatar_url && (form.bot_name?.[0] || 'B')}
                            </div>
                            <div>
                                <div className="text-sm font-semibold leading-tight">{form.bot_name || 'Bot'}</div>
                                <div className="text-[10px] text-slate-500">online</div>
                            </div>
                        </div>
                    ) : <div className="h-6" />}

                    <div className="p-4 space-y-2">
                        <div style={{
                            padding: '12px 16px', borderRadius: bubbleRadius,
                            background: bubbleBg, color: bubbleText,
                            fontSize: 13, lineHeight: 1.45, animationDelay: '.05s',
                        }}>Ola! Sou {form.bot_name || 'a Juliana'}.</div>
                        <div style={{
                            padding: '12px 16px', borderRadius: bubbleRadius,
                            background: bubbleBg, color: bubbleText,
                            fontSize: 13, lineHeight: 1.45,
                        }}>Qual sua opcao?</div>

                        <div className="space-y-2 pt-2">
                            {['Opcao A', 'Opcao B', 'Opcao C'].map(t => (
                                <button key={t} type="button" style={{
                                    width: '100%', padding: '11px 16px', fontSize: 13, fontWeight: 500,
                                    background: btnBg, color: btnColor, border: btnBorder,
                                    borderRadius: btnRadius, cursor: 'default',
                                }}>{t}</button>
                            ))}
                        </div>
                    </div>

                    {(form.terms_url || form.privacy_url || form.footer_html) && (
                        <div className="px-4 py-3 text-center text-[10px] text-slate-500 border-t border-black/5 mt-2">
                            {form.footer_html ? <span dangerouslySetInnerHTML={{ __html: form.footer_html }} /> : (
                                <>
                                    {form.terms_url && <a className="mx-1">Termos</a>}
                                    {form.terms_url && form.privacy_url && <span className="opacity-50">|</span>}
                                    {form.privacy_url && <a className="mx-1">Privacidade</a>}
                                </>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

/* ============ BUILDING BLOCKS ============ */
function Card({ children }: { children: React.ReactNode }) {
    return (
        <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
            {children}
        </div>
    );
}

function CardHeader({ icon, title, subtitle }: { icon: React.ReactNode; title: string; subtitle?: string }) {
    return (
        <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg bg-emerald-50 text-emerald-700 flex items-center justify-center shrink-0">
                {icon}
            </div>
            <div className="min-w-0 flex-1">
                <h4 className="text-sm font-semibold text-slate-900 leading-tight">{title}</h4>
                {subtitle && <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>}
            </div>
        </div>
    );
}

function FieldLabel({ children, hint }: { children: React.ReactNode; hint?: string }) {
    return (
        <div className="flex items-baseline gap-2 mb-1.5">
            <label className="text-xs font-medium text-slate-700">{children}</label>
            {hint && <span className="text-[10px] text-slate-400">{hint}</span>}
        </div>
    );
}

function FieldText({ label, value, onChange, placeholder, hint }: {
    label: string; value: string; onChange: (v: string) => void; placeholder?: string; hint?: string;
}) {
    return (
        <div>
            <FieldLabel hint={hint}>{label}</FieldLabel>
            <Input value={value || ''} onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder} className="h-9" />
        </div>
    );
}

function FieldTextarea({ label, value, onChange, placeholder, rows, hint }: {
    label: string; value: string; onChange: (v: string) => void;
    placeholder?: string; rows?: number; hint?: string;
}) {
    return (
        <div>
            <FieldLabel hint={hint}>{label}</FieldLabel>
            <Textarea value={value || ''} onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder} rows={rows || 3}
                className="font-mono text-xs" />
        </div>
    );
}

function FieldColor({ label, value, onChange, hint }: {
    label: string; value: string; onChange: (v: string) => void; hint?: string;
}) {
    return (
        <div>
            <FieldLabel hint={hint}>{label}</FieldLabel>
            <div className="relative">
                <input type="color" value={value || '#000000'}
                    onChange={(e) => onChange(e.target.value)}
                    className="absolute inset-y-0 left-0 w-9 h-9 rounded-l-md border-y border-l border-slate-300 cursor-pointer p-0
                               [&::-webkit-color-swatch-wrapper]:p-0
                               [&::-webkit-color-swatch]:rounded-l-md
                               [&::-webkit-color-swatch]:border-0" />
                <Input value={value || ''} onChange={(e) => onChange(e.target.value)}
                    className="pl-11 font-mono text-xs h-9 uppercase" />
            </div>
        </div>
    );
}

function SegmentedControl({ value, onChange, options }: {
    value: string;
    onChange: (v: string) => void;
    options: { value: string; label: string }[];
}) {
    return (
        <div className="grid grid-cols-3 gap-1 p-1 bg-slate-100 rounded-md">
            {options.map(o => {
                const active = value === o.value;
                return (
                    <button key={o.value} type="button"
                        onClick={() => onChange(o.value)}
                        className={`px-3 py-1.5 rounded text-xs font-medium transition ${
                            active
                                ? 'bg-white text-slate-900 shadow-sm'
                                : 'text-slate-500 hover:text-slate-700'
                        }`}>
                        {o.label}
                    </button>
                );
            })}
        </div>
    );
}

function ToggleRow({ label, hint, checked, onChange }: {
    label: string; hint?: string; checked: boolean; onChange: (v: boolean) => void;
}) {
    return (
        <label className="flex items-start gap-3 cursor-pointer group">
            <Switch checked={checked} onCheckedChange={onChange} className="mt-0.5" />
            <div className="min-w-0">
                <div className="text-sm font-medium text-slate-800 leading-tight">{label}</div>
                {hint && <div className="text-[10px] text-slate-500 mt-0.5">{hint}</div>}
            </div>
        </label>
    );
}
